// Privacy Consent Manager for GDPR and other privacy law compliance
class PrivacyConsentManager
{
  static instance = null;
  static STORAGE_KEY = 'meeples_privacy_consent';
  static CONSENT_VERSION = '1.0'; // Increment when privacy policy changes

  constructor()
  {
    if( PrivacyConsentManager.instance )
    {
      return PrivacyConsentManager.instance;
    }
    PrivacyConsentManager.instance = this;
  }

  // Check if user has given consent for analytics
  hasAnalyticsConsent()
  {
    const consent = this.getStoredConsent();
    return consent && consent.analytics === true && consent.version === PrivacyConsentManager.CONSENT_VERSION;
  }

  // Check if user has given consent for essential cookies
  hasEssentialConsent()
  {
    const consent = this.getStoredConsent();
    return consent && consent.essential === true && consent.version === PrivacyConsentManager.CONSENT_VERSION;
  }

  // Check if user has seen the consent modal before
  hasSeenConsentModal()
  {
    const consent = this.getStoredConsent();
    return consent && consent.version === PrivacyConsentManager.CONSENT_VERSION;
  }

  // Store user's consent preferences
  setConsent( analyticsConsent, essentialConsent = true )
  {
    const consentData = {
      analytics: analyticsConsent,
      essential: essentialConsent, // Essential cookies are required for site functionality
      version: PrivacyConsentManager.CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    try
    {
      localStorage.setItem( PrivacyConsentManager.STORAGE_KEY, JSON.stringify( consentData ) );
    } catch( error )
    {
      console.warn( 'Could not save privacy consent preferences:', error );
    }
  }

  // Get stored consent data
  getStoredConsent()
  {
    try
    {
      const stored = localStorage.getItem( PrivacyConsentManager.STORAGE_KEY );
      return stored ? JSON.parse( stored ) : null;
    } catch( error )
    {
      console.warn( 'Could not read privacy consent preferences:', error );
      return null;
    }
  }

  // Clear all stored consent (for user to withdraw consent)
  clearConsent()
  {
    try
    {
      localStorage.removeItem( PrivacyConsentManager.STORAGE_KEY );
    } catch( error )
    {
      console.warn( 'Could not clear privacy consent preferences:', error );
    }
  }

  // Check if user needs to see consent modal
  shouldShowConsentModal()
  {
    // Always require consent for the root page (main app) if user hasn't given consent for this version
    // This ensures users can't bypass consent by navigating from sub-routes back to the main app
    if( window.location.pathname === '/' )
    {
      return !this.hasSeenConsentModal();
    }

    // For sub-routes (like /privacy, /rules, /terms), don't show consent modal
    // This allows users to view these informational pages without being blocked
    return false;
  }

  // Check if privacy modal is currently blocking other modals
  isPrivacyModalBlocking()
  {
    return this.shouldShowConsentModal();
  }

  // Get consent summary for display
  getConsentSummary()
  {
    const consent = this.getStoredConsent();
    if( !consent )
    {
      return {
        hasConsent: false,
        analytics: false,
        essential: false,
        timestamp: null
      };
    }

    return {
      hasConsent: true,
      analytics: consent.analytics || false,
      essential: consent.essential || false,
      timestamp: consent.timestamp,
      version: consent.version
    };
  }
}

// Export a singleton instance
export const privacyConsent = new PrivacyConsentManager();
export default PrivacyConsentManager;
